import { Component } from '@theme/component';

/**
 * IT card colour chips — picking a chip re-prices the card in place.
 *
 * The price the card renders belongs to the first size of ONE colourway (see
 * snippets/it-card-colourway.liquid). When a shopper points at a different
 * colour the quoted figure has to follow, otherwise the card advertises one
 * colour's price against another colour's ring. Every chip therefore carries
 * its own colourway's first-size price, already formatted server-side with
 * `money_without_trailing_zeros`, so nothing here has to know about currency.
 *
 * The chips stay real <a> links to the colourway's URL. Only an unmodified
 * left click is intercepted: ctrl/cmd/shift/middle click, "open in new tab"
 * and crawlers all keep the navigation they had before, and the card still
 * works with JavaScript off.
 *
 * Being a custom element it re-initialises itself whenever <results-list>
 * replaces the grid on a facet change, a sort or an infinite-scroll page —
 * connectedCallback runs on insertion — so nothing is delegated from
 * `document` and no listener is left dangling by a morph.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} row - The chip row.
 * @extends Component<Refs>
 */
class ItCardChips extends Component {
  requiredRefs = ['row'];

  /**
   * Re-price the card and move the ring. Wired from each chip's `on:click`.
   * @param {MouseEvent} event
   */
  pick(event) {
    if (event.defaultPrevented) return;
    if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) {
      return;
    }

    const chip = /** @type {HTMLElement | null} */ (
      /** @type {HTMLElement} */ (event.target).closest('[data-colour]')
    );
    if (!chip || !this.contains(chip)) return;

    const price = this.#priceEl();
    // With nothing to switch on this card, leave the click alone and let the
    // chip navigate as it always did. The "+N" chip has no `on:click` at all,
    // so it never reaches here and keeps taking the shopper to the product.
    if (!price && !chip.dataset.image) return;

    event.preventDefault();

    this.#swapImage(chip);
    this.#retargetBag(chip);

    const now = price?.querySelector('[data-it-price-now]');
    const was = price?.querySelector('[data-it-price-was]');
    const amount = chip.dataset.price;
    const compare = chip.dataset.compare;

    if (now && amount) now.textContent = amount;
    if (was) {
      if (compare) {
        was.textContent = compare;
        was.hidden = false;
      } else {
        was.textContent = '';
        was.hidden = true;
      }
    }

    for (const other of this.refs.row.querySelectorAll('[data-colour]')) {
      const active = other === chip;
      other.classList.toggle('is-active', active);
      if (active) {
        other.setAttribute('aria-current', 'true');
      } else {
        other.removeAttribute('aria-current');
      }
    }

    // The card's overlay link, its gallery link and its title still point at
    // the product's default variant, so a shopper who picked Black and then
    // tapped the card itself landed on the default colour. `applyVariantToLinks`
    // is <product-card>'s public API for exactly this and preserves each
    // link's other query params. The variant id is already in the chip's own
    // href, so nothing extra has to be emitted to carry it.
    const query = (chip.getAttribute('href') || '').split('?')[1] ?? '';
    const variantId = new URLSearchParams(query).get('variant');
    const host = /** @type {any} */ (this.closest('product-card'));
    if (variantId && typeof host?.applyVariantToLinks === 'function') {
      host.applyVariantToLinks(variantId);
    }
  }

  /**
   * Repaint the card with the picked colourway's photograph.
   *
   * A card only ever carries ONE colourway's media — every slide in the
   * gallery belongs to whichever colour `it-card-colourway` settled on, and
   * the other colours have no image in the DOM at all. That is why picking a
   * chip used to leave the picture alone: there was nothing to select. So the
   * photo comes from the chip's own `data-image` instead, which costs no
   * request until someone actually picks it.
   *
   * The trailing slides are hidden rather than repainted, because they are
   * further shots of the colour the shopper just moved away from — leaving
   * them swipeable would put Aqua cuffs behind a Red chip, which is the
   * mismatch this is meant to end.
   *
   * @param {HTMLElement} chip
   */
  #swapImage(chip) {
    const source = chip.dataset.image;
    if (!source) return;

    const card = this.#card();
    const slides = card ? [.../** @type {NodeListOf<HTMLElement>} */ (card.querySelectorAll('[slide-id]'))] : [];
    const [first] = slides;
    const image = first?.querySelector('img');
    if (!first || !image) return;

    image.setAttribute('src', source);
    if (chip.dataset.imageSrcset) {
      image.setAttribute('srcset', chip.dataset.imageSrcset);
    } else {
      image.removeAttribute('srcset');
    }
    // It may still be waiting on lazy-loading further down the grid, and this
    // one is now wanted on screen.
    image.removeAttribute('loading');

    for (const slide of slides.slice(1)) slide.hidden = true;

    first.hidden = false;
    const slideshow = /** @type {any} */ (card?.querySelector('slideshow-component'));
    const id = first.getAttribute('slide-id');
    if (id && typeof slideshow?.select === 'function') {
      slideshow.select({ id }, undefined, { animate: false });
    }
  }

  /**
   * Point the bag strip's size buttons at the picked colourway.
   *
   * The strip is rendered server-side for one colourway (see
   * blocks/it-card-bag.liquid), and it-card-bag.js copies the clicked
   * button's `data-variant` straight into the form — so without this, a card
   * showing Red added the original colour to the cart.
   *
   * Sizes the new colourway cannot sell are hidden rather than left pointing
   * at a stale id. The buttons live inside a <template> until the strip is
   * opened, so both that content and any already-cloned panel are updated.
   *
   * @param {HTMLElement} chip
   */
  #retargetBag(chip) {
    const sizes = chip.dataset.sizes;
    // Absent entirely means this card never emitted a map, so leave the strip
    // alone. Present but empty means the opposite and must be acted on: the
    // colourway sells nothing, and leaving the previous colour's buttons in
    // place is how a sold-out chip used to add a different colour.
    if (sizes == null) return;

    const bag = this.#card()?.querySelector('it-card-bag');
    if (!bag) return;

    /** @type {Map<string, string>} */
    const ids = new Map();
    for (const pair of sizes.split('|')) {
      const at = pair.indexOf('=');
      if (at > 0) ids.set(pair.slice(0, at).trim(), pair.slice(at + 1));
    }

    /** @param {ParentNode} root */
    const retarget = (root) => {
      for (const button of /** @type {NodeListOf<HTMLElement>} */ (root.querySelectorAll('[data-variant]'))) {
        const label = button.querySelector('.add-to-cart-text')?.textContent?.trim() ?? '';
        // `*` covers a product with no size option, where the single button is
        // labelled "Add" rather than a size.
        const id = ids.get(label) ?? ids.get('*');
        const holder = /** @type {HTMLElement} */ (button.closest('add-to-cart-component') ?? button);

        if (id) {
          button.dataset.variant = id;
          // A colour that sells this size again clears the sold-out state the
          // previous colourway may have left on the button.
          delete button.dataset.inCart;
          // The ceiling belonged to the previous colourway's variant and says
          // nothing about this one. Shopify still refuses an over-add, so the
          // worst case is losing a pre-emptive message, where keeping a stale
          // number silently retires a size this colour can actually sell.
          delete button.dataset.max;
          holder.hidden = false;
        } else {
          holder.hidden = true;
        }
      }
    };

    for (const template of /** @type {NodeListOf<HTMLTemplateElement>} */ (bag.querySelectorAll('template'))) {
      retarget(template.content);
    }
    retarget(bag);

    // Nothing to buy in this colourway, so the outstanding choice is the
    // colour, not the size. Saying so beats an empty row under "Select size".
    const nothingToSell = ids.size === 0;
    const label = nothingToSell
      ? bag.getAttribute('data-colour-label')
      : bag.getAttribute('data-size-label');

    if (label) {
      /** @param {ParentNode} root */
      const relabel = (root) => {
        const target = root.querySelector('.it-sizepop__label');
        if (target) target.textContent = label;
      };
      for (const template of /** @type {NodeListOf<HTMLTemplateElement>} */ (bag.querySelectorAll('template'))) {
        relabel(template.content);
      }
      relabel(bag);
    }

    bag.classList.toggle('is-choose-colour', nothingToSell);
  }

  /**
   * The card wrapper. Falls back through the search page's different wrapper.
   * @returns {HTMLElement | null}
   */
  #card() {
    return /** @type {HTMLElement | null} */ (
      this.closest('product-card') ?? this.closest('.product-grid__card') ?? this.closest('li') ?? this.parentElement
    );
  }

  /**
   * The price block is a sibling block inside the same card, not a child of
   * this element, so walk up to the card and back down.
   * @returns {HTMLElement | null}
   */
  #priceEl() {
    return this.#card()?.querySelector('[data-it-card-price]') ?? null;
  }
}

if (!customElements.get('it-card-chips')) {
  customElements.define('it-card-chips', ItCardChips);
}
